<script lang="ts" setup>
  import Skeleton from 'primevue/skeleton'
  import { useI18n } from 'vue-i18n'

  type WidgetSkeletonVariant =
    | 'favorites'
    | 'infoboard'
    | 'worktime'
    | 'state'
    | 'table'
    | 'tracking'
    | 'chart'
    | 'service-revenue'
    | 'year-revenue'

  const { t } = useI18n()

  defineProps<{
    variant: WidgetSkeletonVariant
  }>()
</script>

<template>
  <div
    class="h-full max-h-full min-h-0 w-full overflow-hidden p-2"
    role="status"
    aria-live="polite"
    aria-busy="true">
    <span class="sr-only">{{ t('loading') }}</span>

    <div v-if="variant === 'favorites'" class="flex h-full items-start gap-2 overflow-hidden" aria-hidden="true">
      <Skeleton width="7rem" height="2rem" />
      <Skeleton width="9rem" height="2rem" />
      <Skeleton width="6rem" height="2rem" />
      <Skeleton width="8rem" height="2rem" />
    </div>

    <div
      v-else-if="variant === 'infoboard'"
      class="grid h-full min-h-0 grid-cols-1 gap-2 @2xl:grid-cols-2"
      aria-hidden="true">
      <div
        v-for="entry in 6"
        :key="entry"
        class="flex min-h-0 flex-col justify-between rounded border border-gray-200 p-2 dark:border-gray-700">
        <div class="flex items-center gap-2">
          <Skeleton width="1rem" height="1rem" class="shrink-0" />
          <Skeleton :width="entry % 2 ? '58%' : '72%'" height="0.75rem" />
        </div>
        <div class="flex justify-between gap-4">
          <Skeleton width="30%" height="0.5rem" />
          <Skeleton width="24%" height="0.5rem" />
        </div>
      </div>
    </div>

    <div v-else-if="variant === 'worktime'" class="flex h-full flex-col gap-2" aria-hidden="true">
      <Skeleton width="62%" height="0.625rem" class="self-center" />
      <div class="grid grid-cols-2 gap-1">
        <Skeleton v-for="button in 4" :key="button" height="2rem" />
      </div>
      <div class="mt-1 flex flex-col gap-2">
        <div v-for="row in 2" :key="row" class="grid grid-cols-2 gap-3 border-b border-gray-100 pb-1 dark:border-gray-800">
          <Skeleton width="65%" height="0.5rem" class="justify-self-end" />
          <Skeleton width="55%" height="0.5rem" />
        </div>
      </div>
    </div>

    <div v-else-if="variant === 'state'" class="flex h-full flex-col items-center gap-2" aria-hidden="true">
      <Skeleton width="3rem" height="0.625rem" />
      <Skeleton shape="circle" size="4.5rem" />
      <div class="h-px w-full bg-gray-200 dark:bg-gray-700" />
      <div class="grid w-full flex-1 grid-cols-5">
        <div
          v-for="state in 5"
          :key="state"
          class="flex min-w-0 flex-col items-center justify-center gap-2 border-r border-gray-200 last:border-r-0 dark:border-gray-700">
          <Skeleton shape="circle" size="2rem" />
          <Skeleton width="65%" height="0.5rem" />
        </div>
      </div>
    </div>

    <div v-else-if="variant === 'table'" class="flex h-full flex-col gap-2" aria-hidden="true">
      <div class="flex gap-2 border-b border-gray-200 pb-2 dark:border-gray-700">
        <Skeleton width="5.5rem" height="1.5rem" />
        <Skeleton width="5.5rem" height="1.5rem" />
        <Skeleton width="5.5rem" height="1.5rem" />
      </div>
      <div class="flex items-center justify-between bg-gray-50 p-2 dark:bg-gray-900">
        <Skeleton width="7rem" height="0.625rem" />
        <Skeleton width="2rem" height="2rem" />
      </div>
      <div class="grid grid-cols-2 gap-3 border-y border-gray-200 px-2 py-2 dark:border-gray-700">
        <Skeleton width="45%" height="0.625rem" />
        <Skeleton width="35%" height="0.625rem" class="justify-self-end" />
      </div>
      <div v-for="row in 3" :key="row" class="grid grid-cols-2 gap-3 px-2 py-1">
        <Skeleton :width="row % 2 ? '60%' : '42%'" height="0.5rem" />
        <Skeleton width="50%" height="0.5rem" class="justify-self-end" />
      </div>
    </div>

    <div v-else-if="variant === 'tracking'" class="flex h-full flex-col gap-2" aria-hidden="true">
      <div
        v-for="row in 3"
        :key="row"
        class="flex min-h-12 items-center gap-3 rounded border border-gray-200 px-2 dark:border-gray-700">
        <Skeleton shape="circle" size="1.75rem" class="shrink-0" />
        <div class="flex min-w-0 flex-1 flex-col gap-2">
          <Skeleton :width="row % 2 ? '55%' : '68%'" height="0.625rem" />
          <Skeleton width="82%" height="0.5rem" />
        </div>
        <Skeleton width="2.5rem" height="0.75rem" />
      </div>
    </div>

    <div v-else-if="variant === 'chart'" class="flex h-full items-center justify-center gap-6" aria-hidden="true">
      <Skeleton shape="circle" size="8rem" class="shrink-0" />
      <div class="hidden w-32 flex-col gap-3 @sm:flex">
        <Skeleton width="75%" height="0.625rem" />
        <Skeleton width="100%" height="0.625rem" />
        <Skeleton width="62%" height="0.625rem" />
      </div>
    </div>

    <div v-else-if="variant === 'service-revenue'" class="flex h-full flex-col gap-2" aria-hidden="true">
      <Skeleton width="7rem" height="0.625rem" />
      <div class="flex justify-between gap-6">
        <div class="flex flex-col gap-2">
          <Skeleton width="8rem" height="1.5rem" />
          <Skeleton width="5rem" height="0.5rem" />
        </div>
        <div class="flex flex-col items-end gap-2">
          <Skeleton width="6rem" height="1.5rem" />
          <Skeleton width="4rem" height="0.5rem" />
        </div>
      </div>
      <Skeleton width="6rem" height="0.625rem" class="mt-2" />
      <div class="flex justify-between gap-6">
        <Skeleton width="7rem" height="1.125rem" />
        <Skeleton width="5rem" height="1.125rem" />
      </div>
      <Skeleton width="45%" height="0.5rem" class="mt-auto" />
    </div>

    <div v-else class="flex h-full flex-col justify-center gap-3" aria-hidden="true">
      <div v-for="row in 2" :key="row" class="flex items-center justify-between gap-4">
        <div class="flex flex-1 items-center gap-2">
          <Skeleton width="3rem" height="0.625rem" />
          <Skeleton width="7rem" height="1.125rem" />
        </div>
        <Skeleton width="7rem" height="0.625rem" />
      </div>
      <Skeleton width="40%" height="0.5rem" />
    </div>
  </div>
</template>

<i18n>
{
  "de": { "loading": "Daten werden geladen..." },
  "en": { "loading": "Loading data..." }
}
</i18n>
